import { GetPaymentSchedulesQuery } from '@afterpaytouch/portal-api/consumer/paymentSchedule'
import { GetServerSideProps } from 'next'
import { TFunction, useTranslation } from 'next-i18next'
import { createCombinedServerSideProps } from '../src/utils/server/page-props'
import React, { ReactElement, useEffect } from 'react'
import { ExtendedPanel, Head } from '../src/component'
import { LoadingListPage } from '../src/component/LoadingListPage'
import { NoUpcomingPayment } from '../src/component/NoUpcomingPayment/NoUpcomingPayment'
import { UpcomingPayment } from '../src/component/UpcomingPayment/UpcomingPayment'
import { getLayout } from '../src/layouts/MainLayout'
import { ConsumerPage } from '../src/model'
import { useGetPaymentScheduleQuery, useLazyGetPaymentScheduleQuery, usePaymentScheduleHasResults, useConsumerHasAccountLevelPayments } from '../src/state'
import { PAGE_STATUSES, usePageStatus } from '../src/hooks/usePageStatusTypes'
import { TrackPageViewed } from '../src/integrations/amplitude'
import { useHardship } from '../src/hooks'
import { Panel } from '@afterpaytouch/core'

const I18N_NAMESPACE = ['common', 'upcomingPayments', 'index', 'payments', 'account', 'cardScan', 'ach', 'orders']

const useUpcomingPaymentPage = (): {
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  isHardshipDataError: boolean
  trigger: (arg: GetPaymentSchedulesQuery) => void
  refetchHardshipData: () => void
  hasAccountLevelPayments: boolean
  hasPaymentSchedule: boolean
  t: TFunction
} => {
  const { t } = useTranslation(I18N_NAMESPACE)

  const {
    isLoading: isGetPaymentScheduleLoading,
    isSuccess: isGetPaymentScheduleSuccess,
    isError: isGetPaymentScheduleError,
  } = useGetPaymentScheduleQuery({}, { refetchOnMountOrArgChange: true })
  const hasPaymentSchedule = usePaymentScheduleHasResults()
  const [trigger, { isLoading: isLazyGetPaymentScheduleLoading, isSuccess: isLazyGetPaymentScheduleSuccess, isError: isLazyGetPaymentScheduleError }] =
    useLazyGetPaymentScheduleQuery()

  const hasAccountLevelPayments = useConsumerHasAccountLevelPayments()
  const { refetchHardshipData, isHardshipDataError } = useHardship(hasAccountLevelPayments)

  return {
    isLoading: isGetPaymentScheduleLoading || isLazyGetPaymentScheduleLoading,
    isSuccess: isGetPaymentScheduleSuccess || isLazyGetPaymentScheduleSuccess,
    isError: isGetPaymentScheduleError || isLazyGetPaymentScheduleError,
    isHardshipDataError,
    trigger,
    refetchHardshipData,
    hasAccountLevelPayments,
    hasPaymentSchedule,
    t,
  }
}

const UpcomingPaymentsPage: ConsumerPage = () => {
  const { isLoading, isSuccess, isError, isHardshipDataError, hasPaymentSchedule, trigger, refetchHardshipData, hasAccountLevelPayments, t } =
    useUpcomingPaymentPage()
  const [pageStatus, setPageStatus] = usePageStatus()

  const tableCellHeaders = [
    t('upcomingPayments:activeUpcomingPayments:merchant'),
    t('upcomingPayments:activeUpcomingPayments:paymentNo'),
    t('upcomingPayments:activeUpcomingPayments:dueDate'),
    t('upcomingPayments:activeUpcomingPayments:dueIn'),
    t('upcomingPayments:activeUpcomingPayments:amountDue'),
  ]

  useEffect(() => {
    if (isSuccess) {
      setPageStatus(PAGE_STATUSES.SUCCESS)
    }
    if (isError || isHardshipDataError) {
      setPageStatus(PAGE_STATUSES.ERROR)
    }
    if (isLoading) {
      setPageStatus(PAGE_STATUSES.LOADING)
    }
  }, [isSuccess, isError, isLoading, isHardshipDataError])

  const handleRetry = (): void => {
    if (hasAccountLevelPayments) {
      refetchHardshipData()
    } else {
      trigger({})
    }
  }

  const pageTitle = `${t('upcomingPayments:title')} | ${t('common:brandName')}`

  const render = (): ReactElement => {
    switch (pageStatus) {
      case PAGE_STATUSES.SUCCESS:
        if (hasPaymentSchedule) {
          return <UpcomingPayment />
        } else {
          return <NoUpcomingPayment />
        }
      case PAGE_STATUSES.LOADING:
        return <LoadingListPage tableCellHeaders={tableCellHeaders} tableCellWidth={[240, 0, 120]} />
      case PAGE_STATUSES.ERROR:
        return (
          <Panel>
            <Panel.Content>
              <ExtendedPanel.Error onClick={handleRetry} />
            </Panel.Content>
          </Panel>
        )
    }
  }

  return (
    <>
      <Head title={pageTitle} />
      {render()}
      <TrackPageViewed />
    </>
  )
}

UpcomingPaymentsPage.getLayout = getLayout()

export const getServerSideProps: GetServerSideProps = createCombinedServerSideProps(I18N_NAMESPACE)

export default UpcomingPaymentsPage
